'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { DataTableProps, DataTableColumn } from '@/types/components';
import { TitleSection } from '@/components/ui-components/TitleSection';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs/Breadcrumbs';
import { Input } from '@/components/forms/controls/Input/Input';
import { Button } from '@/components/ui-components/Button/Button';
import { FilterModal } from '@/components/modals/FilterModal';
import type { FilterConfig, FilterValues } from '@/components/modals/FilterModal';
import { DateRangePicker } from '@/components/forms/controls/DateRangePicker/DateRangePicker';
import { DataTableSkeleton } from '@/components/skeletons/DataTableSkeleton';
import { SearchIcon as SearchIconComponent, FilterIcon, CloseIconRed, ExpandIcon } from '@/components/icons';
import { theme, i18n } from '@/config';

// ============================================================================
// Styled Components
// ============================================================================

const DataTableWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  box-sizing: border-box;
  min-height: 280px;
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.border.primary};
  border-bottom: 1px solid ${theme.colors.border.primary};
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  
  /* Ensure border-bottom is always visible */
  border-bottom: 1px solid ${theme.colors.border.primary} !important;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;

    > * {
      width: 100%;
    }
  }
`;

const LeftControls = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const RightControls = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 24px;
  margin-left: auto;
`;

const DateRangeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const ClearDateButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${theme.colors.gray[500]};
  transition: all 0.2s;
  flex-shrink: 0;
  
  &:hover {
    color: ${theme.colors.gray[700]};
    background-color: ${theme.colors.gray[100]};
    border-radius: 3px;
  }
  
  &:focus {
    outline: none;
    color: ${theme.colors.gray[700]};
  }
  
  &:active {
    background-color: ${theme.colors.gray[200]};
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const SearchWrapper = styled.div`
  min-width: 250px;
  position: relative;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const SearchIconWrapper = styled.span`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.gray[550]};
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
  height: 24px;
  width: 24px;
  svg{
    width: 100%;
    height: 100%;
  }
`;

const StyledSearchInput = styled(Input)`
  input {
    padding: 6px 16px !important;
    padding-left: 40px !important;
    border: 1px solid ${theme.colors.border.primary} !important;
    border-radius: 4px !important;
    font-size: 14px !important;
    font-family: 'Open Sans', sans-serif !important;
    &::placeholder {
      color: ${theme.colors.gray[550]} !important;
    
    }
    &:focus {
      border-color: ${theme.colors.status.info} !important;
      box-shadow: 0 0 0 3px ${theme.colors.overlay.focusInfo} !important;
    }
    
    &:hover {
      border-color: ${theme.colors.gray[400]} !important;
    }
  }
`;

const FilterButton = styled(Button)<{ $active?: boolean }>`
height: 32px !important;
${({ $active }) =>
    $active &&
    css`
      background-color: ${theme.colors.primary.accent};
      color: ${theme.colors.white};
      border-color: ${theme.colors.primary.accent};

      &:hover:not(:disabled) {
        background-color: ${theme.colors.primary.accentDark};
        border-color: ${theme.colors.primary.accentDark};
      }
    `}
`;

const TitleWrapper = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${theme.colors.gray[200]};
`;

const TitleWrapperBottom = styled.div`
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: ${theme.colors.white};
`;

const StyledTable = styled.table<{ $striped?: boolean; $hoverable?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const TableHead = styled.thead`
tr th {
  background: ${theme.colors.gray[50]};
}
`;

const TableBody = styled.tbody`
  background-color: ${theme.colors.white};
`;

const TableRow = styled.tr<{ 
  $striped?: boolean; 
  $hoverable?: boolean; 
  $clickable?: boolean;
  $index?: number;
  $selected?: boolean;
}>`
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border.primary};
  }

  background-color: ${({ $selected }) => ($selected ? theme.colors.status.infoBg : 'transparent')};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    background-color: ${({ $selected }) => ($selected ? theme.colors.status.infoBgLight : theme.colors.gray[25])};
  }
`;

const TableHeader = styled.th<{ 
  $align?: 'left' | 'center' | 'right';
  $sortable?: boolean;
  $width?: string;
}>`
  padding: 14px 16px;
  text-align: ${({ $align }) => $align || 'center'};
  width: ${({ $width }) => $width || 'auto'};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-weight: ${theme.fontWeights.semibold};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  background: ${theme.colors.white};
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  position: relative;
  font-family: 'Open Sans', sans-serif;
    &:first-child {
    padding-left: 24px;
  }

  ${({ $sortable }) =>
    $sortable &&
    `
    &:hover {
      background: ${theme.colors.gray[50]};
    }
  `}
`;

const HeaderContent = styled.div<{ $align?: string }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => {
    switch ($align) {
      case 'center':
        return 'center';
      case 'right':
        return 'flex-end';
      default:
        return 'flex-start';
    }
  }};
  gap: 8px;
`;

const SortIcon = styled.span<{ $direction?: 'asc' | 'desc'; $active?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 16px;
  transition: all 0.2s ease;
  opacity: ${({ $active }) => ($active ? 1 : 0.5)};

  &::before,
  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 3px solid transparent;
    border-right: 3px solid transparent;
  }

  &::before {
    border-bottom: 4px solid
      ${({ $direction, $active, theme }) => ($direction === 'asc' && $active ? theme.colors.gray[700] : theme.colors.gray[400])};
    margin-bottom: 1px;
  }

  &::after {
    border-top: 4px solid
      ${({ $direction, $active, theme }) => ($direction === 'desc' && $active ? theme.colors.gray[700] : theme.colors.gray[400])};
  }
`;

const TableCell = styled.td<{ 
  $align?: 'left' | 'center' | 'right';
  $width?: string;
  $bold?: boolean;
  $isChild?: boolean;
}>`
  padding: 20px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  vertical-align: middle;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${({ $bold }) => $bold ? theme.fontWeights.bold : theme.fontWeights.normal};
  color: ${theme.colors.gray[900]};
  word-break: break-all;
  font-family: 'Open Sans', sans-serif;
  &:first-child {
    padding-left: ${({ $isChild }) => $isChild ? '60px' : '24px'};
  }
`;

const ChildTableRow = styled.tr`
  background-color: transparent;
  
  &:hover {
    background-color: ${theme.colors.gray[25]};
  }
`;

const ExpandButton = styled.button<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${theme.colors.gray[600]};
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(90deg)'};
  margin-right: 8px;
  vertical-align: middle;
  
  &:hover {
    color: ${theme.colors.gray[900]};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.xl};
  padding-top: ${theme.spacing['3xl']};
  text-align: center;
  color: ${theme.colors.text.secondary};
`;

const LoadingOverlay = styled.div`
  padding: 48px;
  text-align: center;
  color: ${theme.colors.gray.muted};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid ${theme.colors.gray[100]};
  background: ${theme.colors.white};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 4px 8px;
  border: none;
  background-color: transparent;
  color: ${({ $active, theme }) => $active ? theme.colors.status.errorDark : theme.colors.gray[500]};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  font-style: ${({ $active }) => $active ? 'italic' : 'normal'};
  transition: all 0.2s ease;
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    color: ${({ $active, theme }) => $active ? theme.colors.status.errorDarkest : theme.colors.gray[700]};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${theme.colors.gray[400]};
    opacity: 1;
  }
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${theme.colors.gray[500]};

  label {
    font-size: 14px;
    color: ${theme.colors.gray[500]};
    white-space: nowrap;
    cursor: pointer;
  }
`;

const Select = styled.select`
  padding: 6px 10px;
  border: 1px solid ${theme.colors.gray[200]};
  border-radius: 6px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.gray[700]};
  cursor: pointer;
  font-size: 14px;
  height: 36px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.status.info};
  }

  &:hover {
    border-color: ${theme.colors.border.tertiary};
  }
`;

const Ellipsis = styled.span`
  padding: 4px 4px;
  color: ${theme.colors.gray[500]};
  font-size: 14px;
  display: flex;
  align-items: center;
  font-weight: 600;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: ${theme.colors.gradients.primaryAlt};
          color: ${theme.colors.white};
          
          &:hover {
            transform: translateY(-1px);
            box-shadow: ${theme.shadows.accentHover};
          }
        `;
      case 'danger':
        return `
          background: ${theme.colors.status.errorBg};
          color: ${theme.colors.status.errorText};
          
          &:hover {
            background: ${theme.colors.status.errorBgLight};
          }
        `;
      case 'secondary':
      default:
        return `
          background: ${theme.colors.gray[100]};
          color: ${theme.colors.gray[700]};
          
          &:hover {
            background: ${theme.colors.gray[200]};
          }
        `;
    }
  }}
  
  &:active {
    transform: translateY(0);
  }
`;

// ============================================================================
// Validation Functions
// ============================================================================

const validateProps = <T,>(props: DataTableProps<T>): string[] => {
  const warnings: string[] = [];

  // Data integrity
  if (props.data?.some(item => item === null || item === undefined)) {
    warnings.push('Data array contains null or undefined items');
  }

  // Columns validation
  if (!props.columns || props.columns?.length === 0) {
    warnings.push('Columns array must not be empty');
  }

  const columnKeys = new Set();
  props.columns.forEach((col, idx) => {
    if (!col.key || !col.header) {
      warnings.push(`Column at index ${idx} missing required 'key' or 'header'`);
    }
    if (col.header === '') {
      warnings.push(`Column at index ${idx} has empty header`);
    }
    if (columnKeys.has(col.key)) {
      warnings.push(`Duplicate column key: ${String(col.key)}`);
    }
    columnKeys.add(col.key);

    if (col.align && !['left', 'center', 'right'].includes(col.align)) {
      warnings.push(`Invalid align value for column ${String(col.key)}: ${col.align}`);
    }

    if (col.width && col.width !== 'auto' && !/^\d+(\.\d+)?(px|%|rem|em|vw|vh)$/.test(col.width)) {
      warnings.push(`Invalid width format for column ${String(col.key)}: ${col.width}`);
    }

    if (col.render && typeof col.render !== 'function') {
      warnings.push(`Render prop for column ${String(col.key)} must be a function`);
    }
  });

  if (props.columns?.length > 20) {
    warnings.push('More than 20 columns may cause responsive design issues');
  }

  // Pagination validation
  if (props.pagination) {
    if (props.pageSize && (!Number.isInteger(props.pageSize) || props.pageSize < 1)) {
      warnings.push('pageSize must be a positive integer');
    }
    if (props.currentPage && (!Number.isInteger(props.currentPage) || props.currentPage < 1)) {
      warnings.push('currentPage must be >= 1');
    }
    if (props.totalRecords !== undefined && props.totalRecords < 0) {
      warnings.push('totalRecords must be >= 0');
    }
  }

  // Mode-specific validation
  if (props.mode === 'server') {
    if (props.pagination && props.totalRecords === undefined) {
      warnings.push('Server mode with pagination requires totalRecords');
    }
  } else if (props.mode === 'client' || !props.mode) {
    if (props.data?.length > 10000) {
      warnings.push('Large dataset (>10,000 rows) detected. Consider using mode="server"');
    }
  }

  // Sorting validation
  if (props.sortDirection && !['asc', 'desc'].includes(props.sortDirection)) {
    warnings.push(`Invalid sortDirection: ${props.sortDirection}`);
  }

  if (props.sortColumn && !columnKeys.has(props.sortColumn)) {
    warnings.push(`sortColumn "${props.sortColumn}" does not match any column key`);
  }

  // Search validation
  if (props.searchValue && typeof props.searchValue !== 'string') {
    warnings.push('searchValue must be a string');
  }

  // Callback validation
  ['onRowClick', 'onPageChange', 'onSearch', 'onSort'].forEach(callbackName => {
    const callback = props[callbackName as keyof DataTableProps<T>];
    if (callback !== undefined && typeof callback !== 'function') {
      warnings.push(`${callbackName} must be a function`);
    }
  });

  return warnings;
};

// ============================================================================
// Helper Functions
// ============================================================================

const alphanumericSort = (a: unknown, b: unknown, direction: 'asc' | 'desc'): number => {
  // Handle null/undefined
  if (a == null && b == null) return 0;
  if (a == null) return direction === 'asc' ? 1 : -1;
  if (b == null) return direction === 'asc' ? -1 : 1;

  // Date comparison (handles Date objects and date-like strings, e.g., ISO 8601)
  const isDateObjectA = a instanceof Date;
  const isDateObjectB = b instanceof Date;
  if (isDateObjectA && isDateObjectB) {
    const aTime = (a as Date).getTime();
    const bTime = (b as Date).getTime();
    return direction === 'asc' ? aTime - bTime : bTime - aTime;
  }

  const aStrForDate = typeof a === 'string' ? a : undefined;
  const bStrForDate = typeof b === 'string' ? b : undefined;
  const aParsedTime = aStrForDate !== undefined ? Date.parse(aStrForDate) : Number.NaN;
  const bParsedTime = bStrForDate !== undefined ? Date.parse(bStrForDate) : Number.NaN;
  if (!Number.isNaN(aParsedTime) && !Number.isNaN(bParsedTime)) {
    return direction === 'asc' ? aParsedTime - bParsedTime : bParsedTime - aParsedTime;
  }

  // Convert to strings for comparison
  const aStr = String(a).toLowerCase();
  const bStr = String(b).toLowerCase();

  // Try numeric comparison
  const aNum = parseFloat(aStr);
  const bNum = parseFloat(bStr);

  if (!isNaN(aNum) && !isNaN(bNum)) {
    return direction === 'asc' ? aNum - bNum : bNum - aNum;
  }

  // String comparison
  const comparison = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
  return direction === 'asc' ? comparison : -comparison;
};

const searchInRow = <T,>(row: T, columns: DataTableColumn<T>[], query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  return columns.some(col => {
    const value = row[col.key as keyof T];
    if (value == null) return false;
    // Search in raw value - this searches all column values
    return String(value).toLowerCase().includes(lowerQuery);
  });
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * DataTable Component
 * 
 * A comprehensive table component with pagination, search, sorting, and row interaction.
 * Supports both client-side and server-side data management.
 * 
 * @component
 * @example
 * ```tsx
 * // Client-side mode
 * <DataTable
 *   data={teams}
 *   columns={columns}
 *   title="Team Management"
 *   subtitle="Manage your teams"
 *   pagination
 *   searchable
 *   onRowClick={(row) => console.log('Clicked:', row)}
 *   actions={<Button>Add Team</Button>}
 * />
 * 
 * // Server-side mode
 * <DataTable
 *   mode="server"
 *   data={teams}
 *   columns={columns}
 *   currentPage={page}
 *   pageSize={10}
 *   totalRecords={totalCount}
 *   onPageChange={setPage}
 *   onSearch={handleSearch}
 *   onSort={handleSort}
 * />
 * ```
 */
export function DataTable<T = Record<string, unknown>>({
  // Data
  data,
  columns,
  
  // Mode
  mode = 'client',
  
  // Pagination
  pagination = true,
  footerOptions,
  currentPage: controlledPage,
  pageSize: controlledPageSize = 10,
  totalRecords: controlledTotalRecords,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  
  // Search
  searchable = true,
  searchPlaceholder = 'Search...',
  searchValue: controlledSearchValue,
  onSearch,
  
  // Sorting
  sortColumn: controlledSortColumn,
  sortDirection: controlledSortDirection,
  onSort,
  
  // Filtering
  filterable = false,
  filterConfig,
  onFilterApply,
  filterValues,
  showDateRangeInControls = false,
  // Legacy filter props (kept for backward compatibility)
  filterFieldOptions = [],
  selectedFilterFields = [],
  onFilterFieldsChange,
  filterStartDate,
  filterEndDate,
  onFilterDateRangeChange,
  filterMinDate,
  filterMaxDate,
  filterLevelOptions = [],
  selectedFilterLevel = 'all',
  onFilterLevelChange,
  filterLevelLabel = 'Log Level',
  filterSourceOptions = [],
  selectedFilterSource = 'all',
  onFilterSourceChange,
  filterStatusOptions = [],
  selectedFilterStatus = 'all',
  onFilterStatusChange,
  quickLinks,
  
  // Row interaction
  onRowClick,
  rowActions,
  
  // Expandable rows
  isExpandable,
  
  // Header
  title,
  subtitle,
  titleActions,
  
  // Actions
  actions,
  leftActions,
  
  // Styling
  striped = true,
  hoverable = true,
  loading = false,
  
  className,
  style
}: DataTableProps<T>) {
  // ============================================================================
  // Validation
  // ============================================================================
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const warnings = validateProps({ data, columns, mode, pagination, currentPage: controlledPage, 
        pageSize: controlledPageSize, totalRecords: controlledTotalRecords, pageSizeOptions, onPageChange,
        searchable, searchPlaceholder, searchValue: controlledSearchValue, onSearch, 
        sortColumn: controlledSortColumn, sortDirection: controlledSortDirection, onSort,
        onRowClick, title, subtitle, actions, striped, hoverable, loading, className });
      warnings.forEach(warning => console.warn(`[DataTable Warning]: ${warning}`));
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // State Management
  // ============================================================================
  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(controlledPageSize);
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [internalSortColumn, setInternalSortColumn] = useState<string | undefined>(undefined);
  const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  // Date range state for RightControls
  const [dateRangeStart, setDateRangeStart] = useState(filterStartDate || '');
  const [dateRangeEnd, setDateRangeEnd] = useState(filterEndDate || '');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Determine if controlled or uncontrolled
  const isServerMode = mode === 'server';
  const currentPage = isServerMode ? (controlledPage || 1) : internalPage;
  const pageSize = isServerMode ? controlledPageSize : internalPageSize;
  const searchValue = isServerMode ? (controlledSearchValue || '') : internalSearchValue;
  const sortColumn = isServerMode ? controlledSortColumn : internalSortColumn;
  const sortDirection = isServerMode ? controlledSortDirection : internalSortDirection;

  // ============================================================================
  // Data Processing (Client Mode)
  // ============================================================================
  const processedData = useMemo(() => {
    if (isServerMode) {
      return data || [];
    }

    // Defensive: handle undefined/null data
    if (!data || !Array.isArray(data)) {
      return [];
    }

    let result = [...data];

    // Search
    if (searchValue && searchValue?.trim()) {
      result = result.filter(row => searchInRow(row, columns, searchValue));
    }

    // Sort
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        const aValue = a[sortColumn as keyof T];
        const bValue = b[sortColumn as keyof T];
        return alphanumericSort(aValue, bValue, sortDirection);
      });
    }

    return result;
  }, [data, searchValue, sortColumn, sortDirection, columns, isServerMode]);

  // Calculate pagination
  const totalRecords = isServerMode ? (controlledTotalRecords || 0) : processedData?.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  
  // Auto-clamp current page
  const safePage = Math.max(1, Math.min(currentPage, totalPages || 1));
  useEffect(() => {
    if (!isServerMode && safePage !== currentPage) {
      setInternalPage(safePage);
    }
  }, [safePage, currentPage, isServerMode]);

  // Paginated data (client mode only)
  const paginatedData = useMemo(() => {
    if (isServerMode || !pagination) {
      return processedData;
    }
    const startIndex = (safePage - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [processedData, safePage, pageSize, pagination, isServerMode]);

  // ============================================================================
  // Event Handlers
  // ============================================================================
  const handleSearch = useCallback((value: string) => {
    const trimmedValue = value.slice(0, 255); // Max 255 chars
    if (isServerMode) {
      onSearch?.(trimmedValue);
    } else {
      setInternalSearchValue(trimmedValue);
      setInternalPage(1); // Reset to first page on search
    }
  }, [isServerMode, onSearch]);

  const handleSort = useCallback((columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column || column.sortable === false) return;

    let newDirection: 'asc' | 'desc' | undefined = 'asc';
    
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = undefined;
      }
    }

    if (isServerMode) {
      if (newDirection) {
        onSort?.(columnKey, newDirection);
      }
    } else {
      setInternalSortColumn(newDirection ? columnKey : undefined);
      setInternalSortDirection(newDirection);
    }
  }, [columns, sortColumn, sortDirection, isServerMode, onSort]);

  const handlePageChange = useCallback((page: number) => {
    const clampedPage = Math.max(1, Math.min(page, totalPages));
    if (isServerMode) {
      onPageChange?.(clampedPage);
    } else {
      setInternalPage(clampedPage);
    }
  }, [totalPages, isServerMode, onPageChange]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    if (isServerMode) {
      onPageSizeChange?.(newSize); // Notify parent of page size change
      onPageChange?.(1); // Reset to first page
    } else {
      setInternalPageSize(newSize);
      setInternalPage(1);
    }
  }, [isServerMode, onPageSizeChange, onPageChange]);

  const handleRowClick = useCallback((row: T) => {
    onRowClick?.(row);
  }, [onRowClick]);

  const handleRowKeyDown = useCallback((event: React.KeyboardEvent, row: T) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick?.(row);
    }
  }, [onRowClick]);

  // Check if any filter is active (only consider keys present in filterConfig)
  const hasActiveFilters = useMemo(() => {
    // New filter system
    if (filterConfig && filterValues) {
      const configFilterIds = new Set(filterConfig.filters?.map((f) => f.id) ?? []);
      return Object.entries(filterValues).some(([key, value]) => {
        if (!configFilterIds.has(key)) return false;
        // Exclude "all" values and empty/undefined values (treat "all" as inactive/default)
        if (value === 'all' || value === undefined || value === '') {
          return false;
        }
        return true;
      });
    }
    // Date range in controls
    const hasDateRange = Boolean(dateRangeStart || dateRangeEnd);
    return hasDateRange;
  }, [filterConfig, filterValues, dateRangeStart, dateRangeEnd]);

  const handleOpenFilterModal = useCallback(() => {
    setIsFilterModalOpen(true);
  }, []);

  const handleCloseFilterModal = useCallback(() => {
    setIsFilterModalOpen(false);
  }, []);

  // ============================================================================
  // Pagination UI
  // ============================================================================
  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Determine what to show based on current page
      if (safePage <= 3) {
        // Near the beginning: 1, 2, 3, ..., last
        pages.push(2);
        pages.push(3);
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (safePage >= totalPages - 2) {
        // Near the end: 1, ..., last-2, last-1, last
        pages.push('ellipsis');
        pages.push(totalPages - 2);
        pages.push(totalPages - 1);
        pages.push(totalPages);
      } else {
        // In the middle: 1, ..., current-1, current, current+1, ..., last
        pages.push('ellipsis');
        pages.push(safePage - 1);
        pages.push(safePage);
        pages.push(safePage + 1);
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  }, [totalPages, safePage]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <DataTableWrapper className={className} style={style}>
      {/* Breadcrumbs/Title */}
       {title && !subtitle && ((searchable || filterable) ? 
        <Breadcrumbs items={[{ label: title }]} rightSection={titleActions} />:  
       <TitleWrapperBottom>
          <Breadcrumbs items={[{ label: title }]} rightSection={titleActions} />
        </TitleWrapperBottom>
       )} 
      
      {/* Title Section */}
      {title && subtitle && (
        <TitleWrapper>
          <TitleSection title={title} subtitle={subtitle} compact={true} />
        </TitleWrapper>
      )}

      {/* Controls: Search, Filter, Actions */}
     {(searchable || filterable) && (
      <ControlsContainer>
        <LeftControls>
          {searchable && (
            <SearchWrapper>
              <SearchIconWrapper>
                <SearchIconComponent width="16" height="16" />
              </SearchIconWrapper>
              <StyledSearchInput
                type="search"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                fullWidth
                disabled={loading}
              />
            </SearchWrapper>
          )}
          {filterable && filterConfig && (
            <FilterButton 
              variant="outline" 
              size="medium" 
              onClick={handleOpenFilterModal} 
              disabled={loading}
              $active={hasActiveFilters}
            >
                <FilterIcon width={20} height={20} />
              Filter
            
            </FilterButton>
          )}
          {leftActions}
        </LeftControls>

        {(actions || quickLinks || (filterStartDate !== undefined && filterEndDate !== undefined && onFilterDateRangeChange)) && (
          <RightControls>
            {quickLinks}
            {onFilterDateRangeChange && (
              <DateRangeWrapper>
                
                <DateRangePicker
                  startDate={filterStartDate || ''}
                  endDate={filterEndDate || ''}
                  onChange={(start: string, end: string) => {
                    // Convert dates to UTC ISO format before sending to backend
                    const convertToUTC = (dateStr: string): string => {
                      if (!dateStr) return '';
                      // Date is in YYYY-MM-DD format, convert to UTC ISO format
                      // Set time to 00:00:00 UTC for start date, 23:59:59 UTC for end date
                      const date = new Date(dateStr + 'T00:00:00Z');
                      if (isNaN(date.getTime())) return '';
                      return date.toISOString();
                    };
                    
                    const convertEndDateToUTC = (dateStr: string): string => {
                      if (!dateStr) return '';
                      // For end date, set to end of day (23:59:59) in UTC
                      const date = new Date(dateStr + 'T23:59:59Z');
                      if (isNaN(date.getTime())) return '';
                      return date.toISOString();
                    };
                    
                    const startUTC = convertToUTC(start);
                    const endUTC = convertEndDateToUTC(end);
                    onFilterDateRangeChange(startUTC, endUTC);
                  }}
                  minDate={filterMinDate}
                  maxDate={filterMaxDate}
                  size="sm"
                />
                {(filterStartDate || filterEndDate) && (
                  <ClearDateButton
                    onClick={() => {
                      onFilterDateRangeChange('', '');
                    }}
                    type="button"
                    aria-label="Clear date range"
                    title="Clear date range"
                  >
                    <CloseIconRed width={12} height={12} />
                  </ClearDateButton>
                )}
              </DateRangeWrapper>
            )}
            {actions}
          </RightControls>
        )}
      </ControlsContainer>)}

      {/* Table */}
      <TableContainer>
        {loading ? (
          <DataTableSkeleton columns={columns?.length} rows={pageSize} />
        ) : paginatedData?.length === 0 ? (
          <EmptyState>
            {searchValue && searchValue?.trim()?.length > 0 ? (
              <>
                No results found for &quot;{searchValue}&quot;. Try adjusting your search.
              </>
            ) : (
              'No data available'
            )}
          </EmptyState>
        ) : (
          <StyledTable $striped={striped} $hoverable={hoverable}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableHeader
                    key={String(column.key)}
                    $align={column.align}
                    $sortable={column.sortable !== false}
                    $width={column.width || '100px'}
                    onClick={(e) => {
                      // Don't trigger sort if clicking on checkbox or other interactive elements
                      const target = e.target as HTMLElement;
                      if (target.closest('input[type="checkbox"]') || target.closest('button') || target.closest('[role="button"]')) {
                        return;
                      }
                      handleSort(String(column.key));
                    }}
                  >
                    <HeaderContent $align={column.align}>
                      {column.headerRender ? column.headerRender() : (column.name || column.header)}
                      {column.sortable && !column.headerRender && (
                        <SortIcon
                          $direction={sortColumn === column.key ? sortDirection : 'asc'}
                          $active={sortColumn === column.key}
                        />
                      )}
                    </HeaderContent>
                  </TableHeader>
                ))}
                {rowActions && rowActions?.length > 0 && (
                  <TableHeader $align="center" $sortable={false} $width="180px">
                    <HeaderContent $align="center">Action</HeaderContent>
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, rowIndex) => {
                const rowWithChildren = row as T & { children?: T[] };
                const hasChildren = rowWithChildren.children && rowWithChildren.children.length > 0;
                const canExpand = isExpandable ? isExpandable(row) : false;
                const isExpanded = expandedRows.has(rowIndex);
                
                return (
                  <React.Fragment key={rowIndex}>
                    <TableRow
                      $striped={striped}
                      $hoverable={hoverable}
                      $clickable={!!onRowClick || (canExpand && hasChildren)}
                      $index={rowIndex}
                      onClick={() => {
                        if (canExpand && hasChildren) {
                          setExpandedRows((prev) => {
                            const next = new Set(prev);
                            if (next.has(rowIndex)) {
                              next.delete(rowIndex);
                            } else {
                              next.add(rowIndex);
                            }
                            return next;
                          });
                        }
                        if (onRowClick) {
                          handleRowClick(row);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (canExpand && hasChildren && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          setExpandedRows((prev) => {
                            const next = new Set(prev);
                            if (next.has(rowIndex)) {
                              next.delete(rowIndex);
                            } else {
                              next.add(rowIndex);
                            }
                            return next;
                          });
                        }
                        if (onRowClick) {
                          handleRowKeyDown(e, row);
                        }
                      }}
                      tabIndex={(onRowClick || (canExpand && hasChildren)) ? 0 : undefined}
                      role={(onRowClick || (canExpand && hasChildren)) ? 'button' : undefined}
                    >
                      {columns.map((column, colIndex) => {
                        const value = row[column.key as keyof T];
                        let cellContent: React.ReactNode;

                        try {
                          cellContent = column.render
                            ? column.render(value, row, rowIndex)
                            : value != null
                            ? String(value)
                            : '';
                        } catch (error) {
                          // Error is already handled by displaying 'Error' in cell
                          // Only log in development for debugging
                          if (process.env.NODE_ENV === 'development') {
                            console.error(`Error rendering column ${String(column.key)}:`, error);
                          }
                          cellContent = 'Error';
                        }

                        return (
                          <TableCell
                            key={String(column.key)}
                            $align={column.align}
                            $width={column.width || '100px'}
                            $bold={column.bold}
                          >
                            {colIndex === 0 && canExpand && hasChildren && (
                              <ExpandButton
                                $expanded={isExpanded}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  setExpandedRows((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(rowIndex)) {
                                      next.delete(rowIndex);
                                    } else {
                                      next.add(rowIndex);
                                    }
                                    return next;
                                  });
                                }}
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                style={{ marginRight: '8px', display: 'inline-flex', verticalAlign: 'middle' }}
                              >
                                <ExpandIcon size={16} />
                              </ExpandButton>
                            )}
                            {cellContent}
                          </TableCell>
                        );
                      })}
                      {rowActions && rowActions?.length > 0 && (
                        <TableCell $align="center" $width="180px">
                          <ActionButtonsContainer>
                            {rowActions.map((action, actionIndex) => (
                              <ActionButton
                                key={actionIndex}
                                $variant={action.variant || 'secondary'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick(row);
                                }}
                              >
                                {action.label}
                              </ActionButton>
                            ))}
                          </ActionButtonsContainer>
                        </TableCell>
                      )}
                    </TableRow>
                    {/* Render child rows */}
                    {isExpanded && hasChildren && rowWithChildren.children?.map((childRow, childIndex) => {
                      const isLastChild = childIndex === (rowWithChildren.children?.length || 0) - 1;
                      const childRowData = childRow as T & { response_time_ms?: number };
                      return (
                        <ChildTableRow 
                          key={`child-${rowIndex}-${childIndex}`}
                          title={childRowData?.response_time_ms !== undefined ? `Response Time: ${childRowData.response_time_ms}ms` : undefined}
                        >
                          {columns.map((column, colIndex) => {
                            const value = childRow[column.key as keyof T];
                            let cellContent: React.ReactNode;

                            try {
                              cellContent = column.render
                                ? column.render(value, childRow, childIndex)
                                : value != null
                                ? String(value)
                                : '';
                            } catch (error) {
                              if (process.env.NODE_ENV === 'development') {
                                console.error(`Error rendering child column ${String(column.key)}:`, error);
                              }
                              cellContent = 'Error';
                            }

                            return (
                              <TableCell
                                key={String(column.key)}
                                $align={column.align}
                                $width={column.width || '100px'}
                                $bold={column.bold}
                                $isChild={true}
                                style={{
                                  borderBottom: !isLastChild ? `1px solid ${theme.colors.border.primary}` : 'none',
                                  paddingLeft: colIndex === 0 ? '60px' : '16px',
                                  paddingRight: colIndex === columns.length - 1 ? '24px' : '16px',
                                }}
                              >
                                {cellContent}
                              </TableCell>
                            );
                          })}
                          {rowActions && rowActions?.length > 0 && (
                            <TableCell $align="center" $width="180px" $isChild={true} />
                          )}
                        </ChildTableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </StyledTable>
        )}

        {/* Pagination */}
        {pagination && totalRecords > 0 && !loading && (totalRecords > pageSize && totalPages > 1) && (
          <>
          <PaginationContainer>
            <PaginationControls>
              <PageButton
                onClick={() => handlePageChange(safePage - 1)}
                disabled={safePage === 1}
              >
                « Previous
              </PageButton>
              
              {pageNumbers.map((page, idx) =>
                page === 'ellipsis' ? (
                  <Ellipsis key={`ellipsis-${idx}`}>...</Ellipsis>
                ) : (
                  <PageButton
                    key={page}
                    $active={page === safePage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </PageButton>
                )
              )}
              
              <PageButton
                onClick={() => handlePageChange(safePage + 1)}
                disabled={safePage === totalPages}
              >
                Next »
              </PageButton>
            </PaginationControls>

            <PageSizeSelector>
              <label htmlFor="data-table-page-size-select">
                {i18n.common.labels.rows}:
              </label>
              <Select
                id="data-table-page-size-select"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                aria-label={i18n.common.labels.rows}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </PageSizeSelector>
          </PaginationContainer>
             {footerOptions && footerOptions}
             </>
        )}
      </TableContainer>

      {/* Filter Modal */}
      {filterable && filterConfig && (
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={handleCloseFilterModal}
          onApply={(filters: FilterValues) => {
            onFilterApply?.(filters);
            setIsFilterModalOpen(false);
          }}
          config={filterConfig}
          initialValues={filterValues}
          title="Filters"
        />
      )}
    </DataTableWrapper>
  );
}

export default DataTable;

